// Adding these as attributes can lead to some edge cases we don't want to deal with
// We offer a proper control to add classes instead
import { documentStore, uiStore } from "@/store/store-accessor";
import { treeNodeReduce } from "@/lib/tree/tree";
import { TreeNode } from "@/types/tree";
import { getNodeHtmlId } from "@/lib/elements/utils";

const FORBIDDEN_ATTRIBUTE_NAMES = ["class", "id"];
/**
 * Applies to IDs, class names
 * The real rules are less strict, but they can create annoying CSS/querySelector bugs or annoyances we don't wanna deal with
 *
 * Allow alphanumeric, "-", "_", but it can't start with a number
 */
function testHtmlValidCharacters(text: string) {
  return /^[a-zA-Z-_][a-zA-Z-_0-9]*$/g.test(text);
}

// Same as testHtmlValidCharacters but allows for column ":"
// Can't start or end with column
function testHtmlAttributeValidCharacters(text: string) {
  return (
    /^[a-zA-Z-_][a-zA-Z-_:0-9]*$/g.test(text) &&
    !text.startsWith(":") &&
    !text.endsWith(":") &&
    !text.startsWith("-")
  );
}

function isHtmlIdAlreadyUsed(id: string, activeElementId: TreeNode["id"]) {
  if (!id) return false;

  const nodesWithMatchingIds = treeNodeReduce(
    (node, acc: TreeNode[]) => {
      if (getNodeHtmlId(node) === id && node.id !== activeElementId) {
        acc = [...acc, node];
      }

      return acc;
    },
    documentStore.document.tree.root,
    []
  );

  return nodesWithMatchingIds.length;
}

function validateHtmlAttributeName(attributeName: string | undefined) {
  if (attributeName === undefined) return true;

  if (attributeName === "") {
    return "Empty HTML attribute names aren't allowed";
  }

  // trying to add invalid HTML name throws errors.
  if (!testHtmlAttributeValidCharacters(attributeName)) {
    return "You've used an invalid character. Only letters, numbers, '-', '_' and ':' are allowed.";
  }

  if (FORBIDDEN_ATTRIBUTE_NAMES.includes(attributeName.toLowerCase())) {
    return `"${attributeName.toLowerCase()}" is not allowed here. Please use the dedicated control.`;
  }

  return true;
}

function validateHtmlId(id: string | undefined) {
  if (!id) return true;

  // technically an ID can have almost anything but a space, but we don't want to deal with bullshit issues
  // https://stackoverflow.com/a/79022/5993042
  if (!testHtmlValidCharacters(id)) {
    return "You've used an invalid character in your ID. Only letters, numbers, '-' and  '_' are allowed, and it can't start with a number.";
  }

  if (uiStore.activeElementId) {
    const idIsUsed = isHtmlIdAlreadyUsed(id, uiStore.activeElementId);

    if (idIsUsed) {
      return "This ID already exists in other element. IDs must be unique within a page.";
    }
  }

  return true;
}

function validateHtmlClassName(className: string | undefined) {
  if (className === undefined) return true;

  if (className === "") {
    return "Empty HTML class names aren't allowed";
  }

  if (!testHtmlValidCharacters(className)) {
    return "You've used an invalid character in your class name. Only letters, numbers, '-' and '_' are allowed, and it can't start with a number.";
  }

  return true;
}

export function validateNoQuotes(string: string | undefined) {
  if (string === undefined) return true;

  if (/["']/g.test(string)) {
    return `Double '"' or single "'" quotes aren't allowed`;
  }

  return true;
}

export function validateAlphanumericHyphenUnderscore(
  string: string | undefined
) {
  if (!string) return true;

  if (!/^[a-z0-9-_ ]+$/i.test(string)) {
    return `Only English letters, numbers and "-" or "_" are allowed`;
  }

  return true;
}

export function validateNoNewlines(string: string | undefined) {
  if (!string) return true;

  if (/\r|\n/.test(string)) {
    return "Newlines are not allowed";
  }

  return true;
}

export const inputTextValidationRules = {
  validateHtmlAttributeName,
  validateHtmlClassName,
  validateHtmlId,
  validateNoQuotes,
  validateAlphanumericHyphenUnderscore,
  validateNoNewlines
};
